"use client";

import { useRef, useState, useEffect } from "react";
import Matter from "matter-js";
import { Skeleton } from "@/components/ui/skeleton";

interface FallingTextProps {
  text?: string;
  highlightWords?: string[];
  backgroundColor?: string;
  wireframes?: boolean;
  gravity?: number;
  mouseConstraintStiffness?: number;
}

const FallingText: React.FC<FallingTextProps> = ({
  text = "",
  highlightWords = [],
  backgroundColor = "transparent",
  wireframes = false,
  gravity = 1,
  mouseConstraintStiffness = 0.2,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const engineRef = useRef<Matter.Engine | null>(null);
  const runnerRef = useRef<Matter.Runner | null>(null);
  const renderRef = useRef<Matter.Render | null>(null);

  const [effectStarted, setEffectStarted] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [textReady, setTextReady] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const initializeText = () => {
    if (!textRef.current) return;
    const words = text.split(" ");

    const newHTML = words
      .map((word) => {
        const isHighlighted = highlightWords.some((hw) => word.startsWith(hw));
        return `<span
          class="inline-block mx-[2px] select-none ${
            isHighlighted ? "text-black dark:text-white font-bold" : ""
          }"
        >
          ${word}
        </span>`;
      })
      .join(" ");

    textRef.current.innerHTML = newHTML;
    setTextReady(true);
  };

  const cleanupEffect = () => {
    if (renderRef.current) {
      Matter.Render.stop(renderRef.current);
      if (renderRef.current.canvas && canvasContainerRef.current) {
        canvasContainerRef.current.removeChild(renderRef.current.canvas);
      }
      renderRef.current = null;
    }
    if (runnerRef.current) {
      Matter.Runner.stop(runnerRef.current);
      runnerRef.current = null;
    }
    if (engineRef.current) {
      Matter.World.clear(engineRef.current.world, false);
      Matter.Engine.clear(engineRef.current);
      engineRef.current = null;
    }
  };

  const startEffect = () => {
    if (effectStarted) return;
    setEffectStarted(true);
  };

  useEffect(() => {
    if (hasMounted) {
      initializeText();
    }
  }, [text, highlightWords, hasMounted]);

  useEffect(() => {
    if (!effectStarted) return;

    const { Engine, Render, World, Bodies, Runner, Mouse, MouseConstraint } =
      Matter;

    if (!containerRef.current || !canvasContainerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const width = containerRect.width;
    const height = containerRect.height;

    if (width <= 0 || height <= 0) return;

    const engine = Engine.create();
    engine.world.gravity.y = gravity;
    engineRef.current = engine;

    const render = Render.create({
      element: canvasContainerRef.current,
      engine,
      options: {
        width,
        height,
        background: backgroundColor,
        wireframes,
      },
    });
    renderRef.current = render;

    const boundaryOptions = {
      isStatic: true,
      render: { fillStyle: "transparent" },
      friction: 1, // <-- Strong friction to prevent sliding
      frictionStatic: 1, // <-- Super strong static friction
      collisionFilter: {
        category: 0x0002, // optional but isolates walls
      },
    };

    const floor = Bodies.rectangle(
      width / 2,
      height + 50,
      width * 2,
      100,
      boundaryOptions
    );
    const leftWall = Bodies.rectangle(
      -50,
      height / 2,
      100,
      height * 2,
      boundaryOptions
    );
    const rightWall = Bodies.rectangle(
      width + 50,
      height / 2,
      100,
      height * 2,
      boundaryOptions
    );
    const ceiling = Bodies.rectangle(
      width / 2,
      -50,
      width * 2,
      100,
      boundaryOptions
    );

    if (!textRef.current) return;
    const wordSpans = textRef.current.querySelectorAll("span");

    const wordBodies = [...wordSpans].map((elem) => {
      const rect = elem.getBoundingClientRect();

      const x = rect.left - containerRect.left + rect.width / 2;
      const y = rect.top - containerRect.top + rect.height / 2;

      const body = Bodies.rectangle(x, y, rect.width, rect.height, {
        render: { fillStyle: "transparent" },
        restitution: 0.8,
        frictionAir: 0.01,
        friction: 0.2,
      });

      Matter.Body.setVelocity(body, {
        x: (Math.random() - 0.5) * 5,
        y: 0,
      });
      Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.05);

      return { elem, body };
    });

    wordBodies.forEach(({ elem, body }) => {
      elem.style.position = "absolute";
      elem.style.left = `${body.position.x}px`;
      elem.style.top = `${body.position.y}px`;
      elem.style.transform = "translate(-50%, -50%)";
    });

    const mouse = Mouse.create(render.canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: mouseConstraintStiffness,
        render: { visible: false },
      },
    });
    render.mouse = mouse;

    World.add(engine.world, [
      floor,
      leftWall,
      rightWall,
      ceiling,
      mouseConstraint,
      ...wordBodies.map((wb) => wb.body),
    ]);

    const runner = Runner.create();
    runnerRef.current = runner;
    Runner.run(runner, engine);
    Render.run(render);

    const updateLoop = () => {
      wordBodies.forEach(({ elem, body }) => {
        // Clamp position inside container
        const clampedX = Math.max(
          0,
          Math.min(containerRect.width, body.position.x)
        );
        const clampedY = Math.max(
          0,
          Math.min(containerRect.height, body.position.y)
        );

        elem.style.left = `${clampedX}px`;
        elem.style.top = `${clampedY}px`;
        elem.style.transform = `translate(-50%, -50%) rotate(${body.angle}rad)`;
      });
      requestAnimationFrame(updateLoop);
    };

    updateLoop();

    return () => {
      cleanupEffect();
    };
  }, [
    effectStarted,
    gravity,
    wireframes,
    backgroundColor,
    mouseConstraintStiffness,
  ]);

  useEffect(() => {
    const handleResize = () => {
      cleanupEffect();
      setEffectStarted(false); // Important: set effectStarted to false to fully reset
      initializeText(); // Reinitialize clean text
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!hasMounted)
    return (
      <div className="flex flex-col gap-4 items-center">
        <Skeleton className="h-4 w-[150px] sm:w-[250px] md:w-[350px]" />
        <Skeleton className="h-4 w-[100px] sm:w-[200px] md:w-[300px]" />
      </div>
    );

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full cursor-pointer text-center pt-10 md:pt-20 lg:pt-24"
      onMouseOver={startEffect}
    >
      <div
        ref={textRef}
        className="inline-block lg:text-xl md:text-xl text-base px-2 sm:px-5"
        style={{
          lineHeight: 1.4,
        }}
      />
      <div className="absolute inset-0 z-0" ref={canvasContainerRef} />
    </div>
  );
};

export default FallingText;
